import { ApolloQueryResult, MutationResult } from '@apollo/client';
import { LoadingContext, LoadingDoneVariant } from 'contexts';
import { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { generate as shortId } from 'shortid';

export interface LoadingMessages {
  success?: string;
  error?: string;
  warning?: string;
  info?: string;
}
export interface UseLoadingOptions {
  name: string;
  messages?: LoadingMessages | undefined;
}

export interface useGraphLoadingOptions<TData> extends UseLoadingOptions {
  result: Pick<ApolloQueryResult<TData> | MutationResult<TData>, 'data' | 'loading' | 'error'>;
  expectData?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getData?: (data: useGraphLoadingOptions<TData>['result']['data']) => any | undefined | null;
  messages?: LoadingMessages;
}

export const useLoading = ({ name, messages }: UseLoadingOptions) => {
  // only do this when initializing
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const loadingName = useMemo(() => name || shortId(), []);
  // prevents executing the done code if the start hasn't been called.
  const startedRef = useRef(false);
  const { startLoading, doneLoading } = useContext(LoadingContext);

  const start = useCallback(() => {
    startedRef.current = true;
    startLoading(loadingName);
  }, [loadingName, startLoading]);

  const done = useCallback(
    (status: LoadingDoneVariant = 'success') => {
      if (startedRef.current) {
        startedRef.current = false;
        const message = (messages && messages[status]) || undefined;
        doneLoading(loadingName, status, message);
      }
    },
    [doneLoading, loadingName, messages]
  );
  return [start, done] as [start: typeof start, done: typeof done];
};

export const useGraphLoading = <TData>({
  result: { loading, error, data },
  name,
  getData = (resultData: typeof data) => {
    if (!resultData) {
      return null;
    }
    const firstKey = Object.keys(resultData)[0];
    if (!firstKey) {
      return null;
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return ((resultData as any)[firstKey] || null) as any | undefined | null;
  },
  expectData = true,
  messages,
}: useGraphLoadingOptions<TData>) => {
  const [start, done] = useLoading({ name, messages });

  useEffect(() => {
    if (loading && !error) {
      start();
    } else {
      const resultData = getData(data);
      const hasData = resultData !== undefined && resultData !== null;
      done(error ? 'error' : expectData === hasData ? 'success' : 'warning');
    }
    // we only care about when the loading state changes
  }, [data, done, error, expectData, getData, loading, start]);

  // cleanup on unmount
  useEffect(
    () => () => {
      done('success');
    },
    //we want hook to be called only on destroy , so no deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return [start, done] as const;
};
